import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { useNotifications } from '../hooks/useNotifications';
import { toast } from '../lib/safeToast';
import {
  SendHorizontal,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  ArrowUpRight,
  X,
  AlertTriangle,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getApiErrorMessage } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Alert,
  AlertDescription,
} from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Link } from 'react-router-dom';

const XENDIT_CURRENCIES = ['IDR', 'PHP', 'THB', 'VND', 'MYR'];

const Payments = () => {
  const { api, user } = useAuth();
  const { isLive } = useEnvironment();
  const { fetchNotifications } = useNotifications();
  const kycBlocked = isLive && user?.onboarding_complete && user?.kyc_status !== 'approved';
  const [payments, setPayments] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [bankCodes, setBankCodes] = useState([]);
  const [supportedCurrencies, setSupportedCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [detailPaymentId, setDetailPaymentId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    beneficiaryId: '',
    account: '',
    name: '',
    bsb: '',
    bank_code: '',
    country: 'AU',
    reference: '',
    currency: 'AUD',
  });

  const isXenditCurrency = XENDIT_CURRENCIES.includes(formData.currency);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchBeneficiaries = async () => {
      try {
        const res = await api.get('/beneficiaries');
        setBeneficiaries(res.data?.data || []);
      } catch {
        setBeneficiaries([]);
      }
    };
    fetchBeneficiaries();
  }, [api]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [currRes, bankRes] = await Promise.all([
          api.get('/gateway/supported-currencies'),
          api.get('/gateway/bank-codes?country=ID'),
        ]);
        setSupportedCurrencies(currRes.data?.data || []);
        setBankCodes(bankRes.data?.data || []);
      } catch { /* non-critical */ }
    };
    loadMeta();
  }, [api]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const txRes = await api.get('/banking/transactions?type=withdrawal&per_page=50');
      setPayments(txRes.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentDetail = async (paymentId) => {
    if (!paymentId) return;
    setDetailPaymentId(paymentId);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const res = await api.get(`/banking/payments/${paymentId}`);
      const data = res.data?.data ?? res.data;
      setDetailData(typeof data === 'object' ? data : { id: paymentId, raw: data });
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Could not load payment details'));
      setDetailPaymentId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!formData.name.trim()) {
      toast.error('Please enter beneficiary name');
      return;
    }
    
    if (!formData.account.trim()) {
      toast.error('Please enter account number');
      return;
    }
    
    if (isXenditCurrency && !formData.bank_code) {
      toast.error('Please select a bank');
      return;
    }

    setCreateLoading(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
      };
      if (!payload.bank_code) delete payload.bank_code;
      if (!payload.bsb) delete payload.bsb;
      delete payload.beneficiaryId;
      await api.post('/banking/payments', payload);
      toast.success('Payment submitted successfully');
      setShowCreate(false);
      setFormData({
        amount: '',
        beneficiaryId: '',
        account: '',
        name: '',
        bsb: '',
        bank_code: '',
        country: 'AU',
        reference: '',
        currency: 'AUD',
      });
      fetchData();
      fetchNotifications();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Transactions are pending until KYC/B verification is approved.');
      } else {
        toast.error(getApiErrorMessage(error.response?.data?.detail, 'Payment failed'));
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCurrencyBadgeClass = (currency) => {
    const classes = {
      AUD: 'bg-[#E6F7F3] text-[#1C472B]',
      USD: 'bg-blue-50 text-blue-700',
      EUR: 'bg-purple-50 text-purple-700',
      USDC: 'bg-cyan-50 text-cyan-700',
      USDT: 'bg-emerald-50 text-emerald-700',
    };
    return classes[currency] || 'bg-slate-100 text-slate-700';
  };

  const canCreatePayment = (user?.role === 'admin' || user?.role === 'manager' || user?.role === 'merchant_admin') && !kycBlocked;

  const completedCount = payments.filter((p) => p.status === 'completed').length;
  const pendingCount = payments.filter((p) => p.status === 'pending').length;
  const failedCount = payments.filter((p) => p.status === 'failed').length;

  return (
    <Layout>
      <div data-testid="payments-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-extrabold text-[#0F172A] ">
              Payments
            </h1>
            <p className="text-slate-500 mt-1">Create and track outgoing payments</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchData} variant="outline" className="h-11 px-5 rounded-full" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {canCreatePayment && (
              <Button 
                onClick={() => setShowCreate(true)} 
                className="h-11 px-6 rounded-full bg-[#1C472B] hover:bg-[#143620] text-white"
                data-testid="create-payment-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Payment
              </Button>
            )}
          </div>
        </div>

        {kycBlocked && (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertTriangle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>KYC/B verification in progress.</strong> Our team is reviewing your information. Transactions will be available once the review is complete.
            </AlertDescription>
          </Alert>
        )}
        {/* Sandbox Notice */}
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Sandbox Mode:</strong> Payments are simulated. Banking Circle blocks non-production settlement, 
            so payments will show as "pending" or "failed". In production, payments will process normally and status 
            updates will be received via webhooks.
          </AlertDescription>
        </Alert>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#E6F7F3] rounded-2xl flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-[#1C472B]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Completed</p>
                  <p className="text-3xl font-extrabold text-[#0F172A] ">
                    {completedCount}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center">
                  <Clock className="w-7 h-7 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Pending</p>
                  <p className="text-3xl font-extrabold text-[#0F172A] ">
                    {pendingCount}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center">
                  <XCircle className="w-7 h-7 text-[#F08080]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Failed</p>
                  <p className="text-3xl font-extrabold text-[#0F172A] ">
                    {failedCount}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment History Table */}
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <CardHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
            <CardTitle className="text-xl font-bold text-[#0F172A] ">
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#1C472B]" />
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SendHorizontal className="w-10 h-10 text-slate-300" />
                </div>
                <p className="text-lg font-medium text-slate-600 mb-2">No payments yet</p>
                <p className="text-slate-400 mb-6">Create your first payment to get started</p>
                {canCreatePayment && (
                  <Button
                    onClick={() => setShowCreate(true)}
                    className="h-11 px-6 rounded-full bg-[#1C472B] hover:bg-[#143620] text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Payment
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="payments-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Payment</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Beneficiary</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reference</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="text-right px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                      <th className="text-center px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Currency</th>
                      <th className="text-center px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-right px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr
                        key={payment.id}
                        className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer"
                        data-testid={`payment-${payment.id}`}
                        onClick={() => fetchPaymentDetail(payment.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fetchPaymentDetail(payment.id); } }}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              payment.status === 'completed'
                                ? 'bg-[#E6F7F3]'
                                : payment.status === 'pending'
                                ? 'bg-amber-50'
                                : 'bg-red-50'
                            }`}>
                              <ArrowUpRight className={`w-5 h-5 ${
                                payment.status === 'completed'
                                  ? 'text-[#1C472B]'
                                  : payment.status === 'pending'
                                  ? 'text-amber-500'
                                  : 'text-[#F08080]'
                              }`} />
                            </div>
                            <div>
                              <p className="font-semibold text-[#0F172A]">Outgoing</p>
                              <p className="text-xs text-slate-400 font-mono">{payment.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-slate-700">{payment.counterparty}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-mono text-sm text-slate-600">{payment.reference || '—'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-slate-700">{formatDate(payment.date)}</p>
                            <p className="text-xs text-slate-400">{formatTime(payment.date)}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-lg font-bold text-[#F08080]">
                            -{formatCurrency(payment.amount)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getCurrencyBadgeClass(payment.currency)}`}>
                            {payment.currency}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                              payment.status === 'completed'
                                ? 'bg-[#E6F7F3] text-[#1C472B]'
                                : payment.status === 'pending'
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-red-50 text-[#F08080]'
                            }`}
                          >
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => fetchPaymentDetail(payment.id)}
                            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-[#1C472B] transition-colors"
                            title="View full details"
                            aria-label={`View details for payment ${payment.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Payment Modal - compact, no scroll */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCreate(false)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-visible">
              <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#0F172A] ">New Payment</h2>
                  <p className="text-slate-500 text-sm mt-0.5">Send money to a beneficiary</p>
                </div>
                <button onClick={() => setShowCreate(false)} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <form onSubmit={handleCreatePayment} className="px-6 py-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {beneficiaries.length > 0 ? (
                    <div className="col-span-2">
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Send to saved beneficiary</Label>
                      <Select value={formData.beneficiaryId || '__manual__'} onValueChange={(id) => {
                        if (!id || id === '__manual__') { setFormData({ ...formData, beneficiaryId: '', name: '', account: '', bsb: '', bank_code: '', country: 'AU' }); return; }
                        const b = beneficiaries.find((x) => x.id === id);
                        if (b) setFormData({ ...formData, beneficiaryId: id, name: b.type === 'business' ? (b.company_name || '') : (b.full_name || ''), account: b.account_number || '', bsb: b.bsb || '', bank_code: b.bank_code || '', country: b.country || 'AU' });
                      }}>
                        <SelectTrigger className="h-9 rounded-lg border-slate-200 bg-slate-50 text-sm">
                          <SelectValue placeholder="Choose a beneficiary" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__manual__">Manual entry</SelectItem>
                          {beneficiaries.map((b) => (
                            <SelectItem key={b.id} value={b.id}>
                              {b.type === 'business' ? (b.company_name || 'Business') : (b.full_name || 'Unknown')} — {b.bank_name} - {b.account_number}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <p className="col-span-2 text-xs text-slate-500">
                      <Link to="/beneficiaries" className="text-[#1C472B] font-semibold hover:underline">Add beneficiaries</Link> to quickly select recipients.
                    </p>
                  )}

                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Amount</Label>
                    <Input type="number" step="0.01" min="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" className="h-9 rounded-lg border-slate-200 text-sm font-semibold" required data-testid="payment-amount" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Currency</Label>
                    <Select value={formData.currency} onValueChange={(v) => {
                      const isXen = XENDIT_CURRENCIES.includes(v);
                      const country = v === 'IDR' ? 'ID' : v === 'PHP' ? 'PH' : v === 'THB' ? 'TH' : v === 'VND' ? 'VN' : v === 'MYR' ? 'MY' : v === 'AUD' ? 'AU' : formData.country;
                      setFormData({ ...formData, currency: v, country, bsb: isXen ? '' : formData.bsb, bank_code: isXen ? formData.bank_code : '' });
                    }}>
                      <SelectTrigger className="h-9 rounded-lg border-slate-200 text-sm" data-testid="payment-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {supportedCurrencies.length > 0 ? (
                          supportedCurrencies.filter((c) => c.available).map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.flag} {c.code}
                              {c.gateway !== 'local' && <span className="text-xs text-slate-400 ml-1">via {c.gateway === 'xendit' ? 'Xendit' : c.gateway === 'payitnow' ? 'PayItNow' : c.gateway}</span>}
                            </SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="AUD">🇦🇺 AUD</SelectItem>
                            <SelectItem value="USD">🇺🇸 USD</SelectItem>
                            <SelectItem value="EUR">🇪🇺 EUR</SelectItem>
                            <SelectItem value="IDR">🇮🇩 IDR</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Beneficiary Name</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Recipient name" className="h-9 rounded-lg border-slate-200 text-sm" required data-testid="payment-name" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Country</Label>
                    <Select value={formData.country} onValueChange={(v) => setFormData({ ...formData, country: v })}>
                      <SelectTrigger className="h-9 rounded-lg border-slate-200 text-sm" data-testid="payment-country">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AU">Australia</SelectItem>
                        <SelectItem value="US">United States</SelectItem>
                        <SelectItem value="GB">United Kingdom</SelectItem>
                        <SelectItem value="SG">Singapore</SelectItem>
                        <SelectItem value="NZ">New Zealand</SelectItem>
                        <SelectItem value="ID">Indonesia</SelectItem>
                        <SelectItem value="PH">Philippines</SelectItem>
                        <SelectItem value="TH">Thailand</SelectItem>
                        <SelectItem value="MY">Malaysia</SelectItem>
                        <SelectItem value="VN">Vietnam</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Account Number</Label>
                    <Input value={formData.account} onChange={(e) => setFormData({ ...formData, account: e.target.value })} placeholder={isXenditCurrency ? 'e.g. 1234567890' : '12345678'} className="h-9 rounded-lg border-slate-200 font-mono text-sm" required data-testid="payment-account" />
                  </div>
                  {isXenditCurrency ? (
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Bank</Label>
                      <Select value={formData.bank_code} onValueChange={(v) => setFormData({ ...formData, bank_code: v })}>
                        <SelectTrigger className="h-9 rounded-lg border-slate-200 text-sm" data-testid="payment-bank-code">
                          <SelectValue placeholder="Select bank" />
                        </SelectTrigger>
                        <SelectContent>
                          {bankCodes.map((b) => (
                            <SelectItem key={b.code} value={b.code}>{b.name} ({b.code})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">BSB <span className="text-slate-400 font-normal">(Optional)</span></Label>
                      <Input value={formData.bsb} onChange={(e) => setFormData({ ...formData, bsb: e.target.value.replace(/\D/g, '').slice(0, 6) })} placeholder="000000" maxLength={6} className="h-9 rounded-lg border-slate-200 font-mono text-sm" data-testid="payment-bsb" />
                    </div>
                  )}
                  <div className="col-span-2">
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Reference <span className="text-slate-400 font-normal">(Optional)</span></Label>
                    <Input value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} placeholder="Invoice #123 or payment description" className="h-9 rounded-lg border-slate-200 text-sm" data-testid="payment-reference" />
                  </div>
                  <div className="col-span-2 flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="flex-1 h-10 rounded-full text-sm border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">Cancel</Button>
                    <Button type="submit" className="flex-1 h-10 rounded-full bg-[#1C472B] hover:bg-[#143620] text-white font-semibold text-sm" disabled={createLoading} data-testid="submit-payment">
                      {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><SendHorizontal className="w-4 h-4 mr-2" />Send Payment</>}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Payment detail modal - full visibility from PIN APIs */}
        <Dialog open={!!detailPaymentId} onOpenChange={(open) => { if (!open) setDetailPaymentId(null); }}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="text-lg">Transaction details</DialogTitle>
            </DialogHeader>
            {detailLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#1C472B]" />
              </div>
            ) : detailData ? (
              <div id="payment-detail-content" className="space-y-4">
                {Object.entries(detailData).map(([key, value]) => {
                  if (value == null || key.startsWith('_')) return null;
                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                  const display = typeof value === 'object' && value !== null && !Array.isArray(value)
                    ? JSON.stringify(value, null, 2)
                    : String(value);
                  return (
                    <div key={key} className="flex flex-col gap-0.5">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
                      <span className="text-sm font-mono text-slate-800 break-all whitespace-pre-wrap">{display}</span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Payments;
